import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, List, FileText, GitCompare, Play, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import AccordionItem from '@/components/ds/AccordionItem';
import {
  CLAUDE_PAGE_PUBLISHED, CLAUDE_AI_SECTION_VISIBLE, SEO_COPY, HERO, CAPABILITIES,
  CLAUDE_CODE_STEPS, CLAUDE_AI_STEPS, PERMISSIONS, FAQS, CLOSING,
} from '@/pages/claudeContent';
import '@/styles/landing.css';

/**
 * /claude/ — how to use UXAuditPro inside Claude (Claude Code and claude.ai).
 *
 * NOINDEX, UNLINKED, AT ITS FINAL URL until CLAUDE_PAGE_PUBLISHED, the same
 * review path /sample-report/ took. The copy and its sources live in
 * claudeContent.js; this file only lays it out, in the landing vocabulary
 * (.uxa-landing-section, .uxa-feature-card, AccordionItem, .uxa-closing).
 *
 * The FAQ uses AccordionItem because it keeps every answer in the DOM, so the
 * prerendered page carries the answers, not just the questions (the defect
 * UxAuditorPage's FAQ note records).
 */

const ICONS = [List, FileText, GitCompare, Play, ShieldCheck];

function Steps({ steps, testId }) {
  return (
    <ol className="uxa-claude-steps" data-testid={testId}>
      {steps.map((s) => (
        <li key={s.text}>
          <span>{s.text}</span>
          {s.code && <code className="uxa-claude-code">{s.code}</code>}
        </li>
      ))}
    </ol>
  );
}

export default function ClaudePage() {
  const { user, login } = useAuth();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="uxa-landing" data-testid="claude-page">
      <SEO
        title={SEO_COPY.title}
        description={SEO_COPY.description}
        canonical="/claude"
        noIndex={!CLAUDE_PAGE_PUBLISHED}
      />

      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="uxa-hero-eyebrow">{HERO.eyebrow}</span>
        <h1 className="uxa-h1" style={{ margin: 'var(--space-6) auto 0', textWrap: 'balance' }}>{HERO.h1}</h1>
        <p className="uxa-lead" style={{ maxWidth: 620, margin: 'var(--space-5) auto 0', color: 'var(--ink-2)' }}>
          {HERO.lead}
        </p>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '0 0 var(--space-10)' }}>What it does today</h2>
        <div className="uxa-grid-3">
          {CAPABILITIES.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <div key={c.title} className="uxa-feature-card" style={{ padding: 'var(--space-6)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--accent-tint)' }}>
                  <Icon className="w-5 h-5" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </div>
                <h3 className="uxa-h3 mb-2" style={{ color: 'var(--ink-1)' }}>{c.title}</h3>
                <p className="uxa-dense" style={{ color: 'var(--ink-2)', margin: 0 }}>{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Set up in Claude Code</h2>
          <Steps steps={CLAUDE_CODE_STEPS} testId="claude-code-steps" />

          {CLAUDE_AI_SECTION_VISIBLE && (
            <>
              <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 'var(--space-16) 0 0' }}>Set up in claude.ai</h2>
              <Steps steps={CLAUDE_AI_STEPS} testId="claude-ai-steps" />
            </>
          )}

          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 'var(--space-16) 0 0' }}>ChatGPT</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 'var(--space-4) 0 0' }}>Coming soon.</p>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Permissions and safety</h2>
          <ul className="uxa-claude-perms" data-testid="claude-permissions">
            {PERMISSIONS.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span className="section-label">FAQ</span>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px 0 0' }}>Questions &amp; answers</h2>
          <div style={{ marginTop: 40 }}>
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={faq.q}
                question={faq.q}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              >
                {faq.a}
              </AccordionItem>
            ))}
          </div>
        </div>
      </section>

      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>{CLOSING.h2}</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>{CLOSING.lead}</p>
          <div style={{ position: 'relative', display: 'inline-block', marginTop: 40, isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
              {user ? (
                <Link to="/audit/new" data-testid="claude-cta" className="uxa-submit uxa-submit--auto">
                  {CLOSING.cta}
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              ) : (
                <button type="button" onClick={login} data-testid="claude-cta" className="uxa-submit uxa-submit--auto">
                  {CLOSING.cta}
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </button>
              )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
